import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import "babel-polyfill";
import i18n from "i18next";
import "prismjs";
import { QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { initReactI18next } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NetworkErrorModalWrapper } from "src/components/Modals/NetworkErrorModalProvider";
import ErrorBoundaryFallback from "src/components/NewComponents/Errors/ErrorBoundary";
import { ScrollToTop } from "src/components/ScrollToTop";
import { isLekEn } from "src/helpers/services";
import { FontSizeProvider } from "src/hooks/useFontSize";
import { queryClient } from "src/queryClient";
import "src/styles/main.scss";
import M3ThemeProvider from "src/theme/m3/M3ThemeProvider";
import { en, pl } from "src/translations";

const cache = createCache({
	key: "css",
	prepend: true,
});

i18n.use(initReactI18next).init({
	resources: {
		en,
		pl,
	},
	lng: isLekEn() ? "en" : "pl",
	fallbackLng: "pl",
	interpolation: {
		escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
	},
});

interface AppProvidersProps {
	children: ReactNode;
}

/** Wspolny szkielet providerow dla obu entry (src/index.jsx = panel, src/admin/main.tsx = admin). */
export function AppProviders({ children }: AppProvidersProps) {
	return (
		<QueryClientProvider client={queryClient}>
			<CacheProvider value={cache}>
				<M3ThemeProvider>
					<FontSizeProvider>
						<CssBaseline enableColorScheme />
						<ErrorBoundary fallback={<ErrorBoundaryFallback />}>
							<NetworkErrorModalWrapper>
								<BrowserRouter>
									<ScrollToTop />
									{children}
								</BrowserRouter>
							</NetworkErrorModalWrapper>
							<ToastContainer />
						</ErrorBoundary>
					</FontSizeProvider>
				</M3ThemeProvider>
			</CacheProvider>
		</QueryClientProvider>
	);
}
